import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { ResizeHandleState } from '@helios/generic-components'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  RootState,
  selectActiveSession,
  selectFirstLoadedAccountId,
  selectFlexExternalTransactionsSessionCacheStatus,
  selectFlexTransactionsSessionCacheStatus,
  selectIsEnfusionSession,
  SessionCacheStatus,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ComponentContainer } from 'golden-layout'
import { combineLatest, Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { GridRole } from './models/source-key.model'
import { WorkbenchStoreService } from './services/workbench-store.service'
import { MatchingService } from './services/matching.service'
import { HttpTradeReadService } from './services/http-trade-read.service'
import { HttpGlEntryReadService } from './services/http-gl-entry-read.service'
import { HttpTransactionMatchesService } from './services/http-transaction-matches.service'
import { HttpDialogListValuesService } from './services/http-dialog-list-values.service'
import { HttpWorkbenchDataSource } from './services/http-workbench-data-source'
import { AggregateInfoService } from './services/aggregate-info.service'
import { WorkbenchDataSource } from './services/workbench-data-source'
import { FieldCatalogService } from './services/field-catalog.service'
import { HttpFieldCatalogService } from './services/http-field-catalog.service'
import {
  StaticTransactionDerivationService,
  TransactionDerivationSource,
} from './services/transaction-derivation.service'
import {
  HttpTransactionDerivationService,
  USE_HTTP_TRANSACTION_DERIVATION,
} from './services/http-transaction-derivation.service'
import {
  StaticTransactionMetadataService,
  TransactionMetadataSource,
} from './services/transaction-metadata.service'
import {
  HttpTransactionMetadataService,
  USE_HTTP_TRANSACTION_METADATA,
} from './services/http-transaction-metadata.service'
import {
  StaticTransactionValueService,
  TransactionValueSource,
} from './services/transaction-value.service'
import { TransactionActionsService } from './services/transaction-actions.service'
import { HttpTransactionActionsService } from './services/http-transaction-actions.service'
import { TransactionSaveService } from './services/transaction-save.service'
import { HttpTransactionSaveService } from './services/http-transaction-save.service'

interface Transactions2PanelState {
  resizeHandleState: ResizeHandleState
}

/**
 * Composite host for Transactions 2.0. Stacks a Comparison grid over a Base grid with a draggable
 * splitter, mirroring the classic Transactions panel host. The two grids are coordinated entirely
 * through the panel-scoped {@link WorkbenchStoreService} (source assignment, selection sync,
 * cross-filter, related-row highlighting) — they never reference each other directly.
 *
 * The grids' column catalogs come from the live Flex Transactions field-catalog API via
 * {@link HttpFieldCatalogService} — there is no demo catalog in the running panel. The account
 * loaded into the active session is pushed into the store, which the grids react to; a session
 * moving to another account invalidates the account-scoped (Enfusion) catalog and refetches,
 * because the Enfusion column set is resolved per CW client and must never be reused across
 * accounts.
 *
 * There is no demo data on the grid path. Rows come from the Flex Transactions read
 * API via {@link HttpWorkbenchDataSource}, fetched per source once that source's catalog has
 * resolved — the catalog is what names the fields the query selects. Clearwater has neither a
 * catalog nor a read endpoint, so it renders empty.
 *
 * Demo data is confined to the Create/Edit Transaction modal's three seams, and to those only:
 * {@link StaticTransactionMetadataService} (the checked-in field metadata),
 * {@link StaticTransactionValueService} (lookup lists, create defaults and the Prometheus comparison
 * sample column) and
 * {@link StaticTransactionDerivationService} (the replies the metadata's Derivation Parameters
 * endpoints will return). None touches the grid's rows or columns, all three are bound as providers
 * below, and swapping any for a real endpoint is a provider change
 * and nothing else. That is acceptable because Phase 1 of this panel ships `hideInProduction: true`
 * (`src/index.ts`) — the modal is reachable in a demo realm and never by a production user — and it
 * stops being acceptable the moment that flag comes off, which is what makes the two seams the whole
 * of the remaining work rather than a search.
 *
 * The panel is gated on a loaded session, because every endpoint it calls needs a real
 * `helios-session-id` and the read endpoints block until that session has finished loading.
 * Selecting an account in the account list neither creates a session nor re-keys this panel — the
 * account is read from the session itself, so changing the selection while a session is open leaves
 * the open session's catalogs and rows alone.
 */
@Component({
  selector: 'hs-transactions-2',
  templateUrl: './transactions-2-panel.component.html',
  styleUrls: ['./transactions-2-panel.component.scss'],
  providers: [
    WorkbenchStoreService,
    MatchingService,
    AggregateInfoService,
    { provide: WorkbenchDataSource, useClass: HttpWorkbenchDataSource },
    { provide: FieldCatalogService, useClass: HttpFieldCatalogService },
    // PVAL-26492: whole-trade reads that prefill the Edit dialog. A concrete class rather than a
    // seam — there is no demo implementation and nothing chooses between two.
    HttpTradeReadService,
    // PVAL-26685: its GL Entry counterpart — whole journal entries, read by GL entry id.
    HttpGlEntryReadService,
    // PVAL-26451: the edited Enfusion transaction's server-matched Prometheus counterparts, which
    // feed the Edit dialog's Custody/Trading comparison columns. Panel-scoped like the trade read.
    HttpTransactionMatchesService,
    // PVAL-26443: fills in the `catalog` lists the metadata document serves without their options.
    // Injects `TransactionMetadataSource`, provided below — order does not matter, an injector
    // resolves by token rather than by position.
    HttpDialogListValuesService,
    // The Create/Edit Transaction modal's two seams. Phase 1 binds the checked-in metadata config
    // and value fixtures as *running* implementations — swapping either for a real endpoint is a
    // provider change here and nothing else.
    {
      provide: TransactionMetadataSource,
      // Flip USE_HTTP_TRANSACTION_METADATA to false to serve the checked-in catalogue again.
      useClass: USE_HTTP_TRANSACTION_METADATA
        ? HttpTransactionMetadataService
        : StaticTransactionMetadataService,
    },
    {
      provide: TransactionValueSource,
      useClass: StaticTransactionValueService,
    },
    {
      provide: TransactionDerivationSource,
      // PVAL-26685: derivations resolve through `dialog-metadata/resolve`. Flip
      // USE_HTTP_TRANSACTION_DERIVATION to false to answer them from canned values instead.
      useClass: USE_HTTP_TRANSACTION_DERIVATION
        ? HttpTransactionDerivationService
        : StaticTransactionDerivationService,
    },
    {
      provide: TransactionActionsService,
      useClass: HttpTransactionActionsService,
    },
    // PVAL-26440: the Create/Edit/Post dialogs' real Save. Resolved by the modals through the
    // `injector` the grid opens them with.
    {
      provide: TransactionSaveService,
      useClass: HttpTransactionSaveService,
    },
  ],
})
export class Transactions2PanelComponent
  extends BasePanelComponent<Transactions2PanelState>
  implements OnInit, OnDestroy
{
  readonly comparisonRole: GridRole = 'comparison'
  readonly baseRole: GridRole = 'base'

  /**
   * Whether the active session is Enfusion-sourced. Transactions 2.0 has nothing to show for a CW
   * session — no fetch was ever going to succeed against it — so the template gates the whole
   * workbench on this rather than rendering an empty grid shell (headers, toolbar, "No Data") for a
   * session this panel fundamentally does not apply to.
   */
  isEnfusionSession = false

  /**
   * Session-cache status behind each grid's role — Base is bound to Enfusion (Flex Transactions),
   * Comparison to Prometheus (Flex External Transactions), because each role is currently
   * *selectable* as only that one source (see `disabledRoles` in `source-registry.ts`). That
   * restriction is picker-level only: `WorkbenchStoreService.setSource` here checks the looser
   * `ROLE_ALLOWED` list (`workbench-store.service.ts:91`), not `isSourceSelectable` the way the
   * positions-2 twin does — so a role can end up holding a source this cache-status binding does
   * not describe (e.g. Base showing Clearwater while still gated on the Flex Transactions cache).
   * No path in the current picker UI reaches this, so it is latent, not live; untracked — no ticket
   * has been filed for tightening this guard to match positions-2. Fed straight into
   * `<hs-cache-load-listener>` per grid in the template, same mechanism Transactions 1.0 uses: the
   * grid is created once its status releases the listener (Partial, Done or PostLoadDone — see
   * below), so nothing inside `WorkbenchGridComponent` needs to gate its own fetch.
   *
   * `<hs-cache-load-listener>` also releases on Partial, same as 1.0. Accepted deliberately: this
   * panel reconciles two independently-loaded grids, so a grid that fetches while its cache is only
   * Partial can publish an incomplete row set into the shared match index, reading as false
   * unmatched/breaks in the sibling grid — and once mounted, the grid never re-fetches on a later
   * Done, so that can persist until an account or source change re-triggers it. Matching 1.0's
   * behavior was judged worth that risk over building a stricter gate.
   */
  baseCacheStatus = SessionCacheStatus.UNKNOWN
  comparisonCacheStatus = SessionCacheStatus.UNKNOWN

  /** Diagnostics `<hs-cache-load-listener>` surfaces on its "Cache Load Error" card, matching what
   *  every 1.0 panel that wraps in it already passes. */
  currentSessionId?: string
  currentAccountIds?: number[]

  private readonly destroy$ = new Subject<void>()

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: WorkbenchStoreService,
    private readonly fieldCatalog: FieldCatalogService,
    private readonly rootStore: Store<RootState>,
  ) {
    super(container)
  }

  ngOnInit(): void {
    // The account comes from the active session, not from the Accounts panel. Selecting a different
    // account while a session is open leaves this alone; only creating or switching a session moves
    // it, which is what keeps the catalog and row fetches behind session creation.
    //
    // Flex Transactions has no data for a CW session — the backend gates the read/readiness
    // endpoints to SessionKind.ENFN — so this panel must not focus an account loaded into one:
    // focusing it would either 400 against the catalog or leave the grids waiting on a readiness
    // signal the backend will never emit. Passing null instead reuses the existing no-account-
    // focused path (empty computed columns, no fetch, no mask) rather than adding a new state for
    // it.
    combineLatest([
      this.rootStore.select(selectFirstLoadedAccountId),
      this.rootStore.select(selectIsEnfusionSession),
    ])
      .pipe(
        // A session update that leaves its accounts alone (commands, recon date, pin) must not
        // re-trigger the column and row pipelines.
        distinctUntilChanged(([a, ea], [b, eb]) => a === b && ea === eb),
        takeUntil(this.destroy$),
      )
      .subscribe(([accountId, isEnfusionSession]) => {
        this.isEnfusionSession = isEnfusionSession
        this.onAccountIdChange(isEnfusionSession ? accountId ?? null : null)
      })

    this.rootStore
      .select(selectFlexTransactionsSessionCacheStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => (this.baseCacheStatus = status))

    this.rootStore
      .select(selectFlexExternalTransactionsSessionCacheStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => (this.comparisonCacheStatus = status))

    this.rootStore
      .select(selectActiveSession)
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.currentSessionId = session?.id
        this.currentAccountIds = session?.loadedAccountIds
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onResizeFinished(resizeHandleState: ResizeHandleState): void {
    this.setState({ resizeHandleState })
  }

  /**
   * React to the session's account changing. Invalidate the previous account's cached catalog so
   * the account-specific Enfusion catalog is refetched and never leaks across accounts, then push
   * the new account into the store for the grids to consume.
   */
  onAccountIdChange(accountId: number | null): void {
    const previous = this.store.accountIdValue()
    if (previous != null && previous !== accountId) {
      this.fieldCatalog.invalidate(previous)
    }
    this.store.setAccountId(accountId)
  }
}
